<script setup>
/* global axios */
import { ref, watch, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { automationMessages, inputClass, buttonClass, primaryClass } from './automation-ui';
const props = defineProps({ conversationId: { type: Number, required: true } });
const route = useRoute();
const { t: translate } = useI18n({ messages: automationMessages });
const t = key => translate(`AUTO.${key}`);
const enabled = ref(false);
const mode = ref('answer');
const input = ref('');
const result = ref(null);
const status = ref('');
const notice = ref('');
const busy = ref(false);
let epoch = 0;
let timer;
let requestId;
const base = () => `/api/v1/accounts/${route.params.accountId}/chat_automation`;
async function poll(version, attempt = 0) {
  try {
    const { data } = await axios.get(`${base()}/ai/requests/${requestId}`);
    if (version !== epoch) return;
    status.value = data.status;
    if (['pending', 'working'].includes(data.status) && attempt < 70) {
      timer = setTimeout(() => poll(version, attempt + 1), 3000);
      return;
    }
    result.value = data.result;
    notice.value = data.result?.error || '';
    busy.value = false;
  } catch (e) {
    if (version !== epoch) return;
    notice.value = e.response?.data?.error || e.message;
    busy.value = false;
  }
}
async function generate() {
  if (busy.value) return;
  const version = epoch;
  busy.value = true;
  notice.value = '';
  result.value = null;
  try {
    const { data } = await axios.post(`${base()}/ai/requests`, { conversation_id: props.conversationId, mode: mode.value, input: input.value });
    if (version !== epoch) return;
    requestId = data.id;
    status.value = data.status;
    await poll(version);
  } catch (e) {
    if (version !== epoch) return;
    notice.value = e.response?.data?.error || e.message;
    busy.value = false;
  }
}
function insert() {
  if (!result.value?.answer) return;
  emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, result.value.answer.replaceAll('<', '&lt;').replaceAll('>', '&gt;'));
}
async function copy() {
  try { await navigator.clipboard.writeText(result.value.answer); notice.value = t('copied'); }
  catch (e) { notice.value = e.message; }
}
watch(() => [route.params.accountId, props.conversationId], async () => {
  const version = ++epoch;
  clearTimeout(timer);
  input.value = ''; result.value = null; status.value = ''; notice.value = ''; busy.value = false; enabled.value = false;
  try {
    const { data } = await axios.get(`${base()}/ai/available`);
    if (version === epoch) enabled.value = data.enabled;
  } catch (e) { if (version === epoch) notice.value = e.response?.data?.error || e.message; }
}, { immediate: true });
onBeforeUnmount(() => { epoch += 1; clearTimeout(timer); });
</script>
<template>
  <details class="max-h-[36rem] shrink-0 overflow-auto border-b border-n-weak px-4 py-3">
    <summary class="cursor-pointer text-sm font-semibold">{{ t('assistant') }}</summary>
    <p v-if="!enabled" class="mt-3 text-xs text-n-slate-10">{{ t('aiDisabled') }}</p>
    <div v-else class="mt-3 space-y-3">
      <select v-model="mode" :aria-label="t('mode')" :class="inputClass" :disabled="busy"><option v-for="item in ['answer', 'summary', 'rewrite']" :key="item" :value="item">{{ t(item) }}</option></select>
      <label v-if="mode !== 'summary'" class="block text-xs">{{ t('input') }}<textarea v-model="input" :class="inputClass" rows="3" maxlength="4000" :disabled="busy" /></label>
      <button :class="primaryClass" :disabled="busy" @click="generate">{{ t('generate') }}</button>
      <p v-if="busy" class="text-xs">{{ t('waitAi') }}</p>
      <p v-if="notice" class="text-xs text-n-orange-11">{{ notice }}</p>
      <template v-if="result?.answer">
        <p class="text-xs text-n-orange-11">{{ t('review') }}</p>
        <textarea :value="result.answer" :aria-label="t('answer')" readonly rows="8" :class="inputClass" />
        <p v-if="result.grounded === false" class="text-xs">{{ t('noGrounding') }}</p>
        <ul v-if="result.sources?.length" class="list-disc ps-4 text-xs"><li v-for="source in result.sources" :key="source.id">{{ source.title }}</li></ul>
        <div class="flex flex-wrap gap-2"><button :class="buttonClass" @click="insert">{{ t('insert') }}</button><button :class="buttonClass" @click="copy">{{ t('copy') }}</button></div>
      </template>
    </div>
  </details>
</template>
